import React from 'react';
import { graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  brandFonts,
  fontSize,
  fontWeights,
  maxBreakpointQuery,
  minBreakpointQuery,
  sectionPaddings,
  standardColours,
  visuallyHidden,
} from '../styles';
import { Container, Heading } from './ui';
import stitchPatternBg from '../images/stitch-pattern-bg.svg';

const StyledIconContentBlocks = styled.section`
  ${sectionPaddings('50px', '100px')};
  text-align: center;

  ${({ altBackground }) => {
    if (altBackground) {
      return css`
        background-color: ${standardColours.lighterGrey};
      `;
    } else {
      return css`
        background: url(${stitchPatternBg}) 0 0 / 160px 160px;
      `;
    }
  }}
`;

const StyledItems = styled.div`
  margin-top: 50px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 40px;

  ${minBreakpointQuery.small`
    gap: 60px;
  `}

  ${minBreakpointQuery.medium`
    margin-top: 70px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-top: 60px;
    gap: 80px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 80px;
    column-gap: 140px;
    row-gap: 100px;
  `}

  ${minBreakpointQuery.xlarge`
    margin-top: 100px;
    column-gap: 160px;
    row-gap: 120px;
  `}
`;

const StyledItem = styled.div`
  ${minBreakpointQuery.mlarge`
     display: flex;
     align-items: center;
     text-align: left;
  `}
`;

const StyledIcon = styled.img`
  width: 90px;
  height: 90px;

  ${maxBreakpointQuery.mlarge`
    margin-bottom: 35px;
  `}

  ${maxBreakpointQuery.smedium`
    margin-bottom: 30px;
  `}

  ${maxBreakpointQuery.tsmall`
    margin-bottom: 25px;
  `}

  ${minBreakpointQuery.mlarge`
    margin-right: 40px;
    width: 100px;
    height: 100px;
  `}
`;

const StyledContent = styled.div``;

const StyledHeading = styled.h3`
  ${fontSize(20)};
  font-family: ${brandFonts.secondary};
  font-weight: ${fontWeights.regular};

  ${minBreakpointQuery.mlarge`
    ${fontSize(24)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(26)};
  `}

  ${minBreakpointQuery.xlarge`
    ${fontSize(30)};
  `}
`;

const StyledText = styled.p`
  ${({ showOnMobile }) => {
    if (showOnMobile) {
      return css`
        margin-top: 16px;
        line-height: 1.5;
      `;
    } else {
      return css`
        ${maxBreakpointQuery.mlarge`
          ${visuallyHidden()}
        `}

        ${minBreakpointQuery.mlarge`
          margin-top: 16px;
          line-height: 1.5;
        `}
      `;
    }
  }}

  ${minBreakpointQuery.large`
    margin-top: 20px;
    ${fontSize(18)};
  `}
`;

const IconContentBlocks = ({ heading, items, textOnMobile, altBackground }) => (
  <StyledIconContentBlocks altBackground={altBackground}>
    <Container>
      <Heading>{heading}</Heading>
      <StyledItems>
        {items.map(({ id, icon, heading, text }) => (
          <StyledItem key={id}>
            <StyledIcon src={icon.url} alt={icon.alt} loading="lazy" />
            <StyledContent>
              <StyledHeading>{heading}</StyledHeading>
              {text && (
                <StyledText showOnMobile={textOnMobile}>{text}</StyledText>
              )}
            </StyledContent>
          </StyledItem>
        ))}
      </StyledItems>
    </Container>
  </StyledIconContentBlocks>
);

export default IconContentBlocks;

export const IconContentBlocksFragment = graphql`
  fragment IconContentBlocksFragment on DatoCmsIconContentBlock {
    id
    icon {
      url
      alt
    }
    heading
    text
  }
`;
